/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Alert, Box, Grid, Typography } from '@mui/material';
import {
  DatePickerV2,
  DialogDefaultV2,
  InputV2,
  TabFormV2,
  UsuarioSelectV2,
} from '../../../components/v2';
import * as actionsRequisicao from '../../../store/requisicao/actions';
import * as selectorsRequisicao from '../../../store/requisicao/reducer';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { isRequisicaoAdmin, canOperarAreaRequisicao } from './permissoes';
import {
  areasDoRascunho,
  catalogoDaArea,
  enderecoEntregaPayload,
  enderecoOutroIncompleto,
  formatEnderecoEntrega,
  REQUISICAO_AREA_LABELS,
} from './requisicaoArea';
import EnderecoEntregaFields from './EnderecoEntregaFields';
import {
  canCriarRequisicaoEntrega,
  canDecidirItens,
  getEntregaAtencao,
  getRequisicaoItems,
  getRequisicaoStatusHelp,
  getRequisicaoStatusLabel,
  hasEntregaCriada,
  isRequisicaoAguardando,
  isRequisicaoCriador,
  isRequisicaoTerminal,
} from './statusLabels';
import ItensTab from './ItensTab';
import EntregasTab from './EntregasTab';
import PedidosTab from './PedidosTab';
import MotivoDialog from './MotivoDialog';
import DecisaoItemDialog from './DecisaoItemDialog';
import ComoFuncionaDialog from './ComoFuncionaDialog';
import CriarEntregasDialog from './CriarEntregasDialog';
import DespacharDialog from './DespacharDialog';

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '—';
};

const emptyCreate = (solicitanteId) => ({
  descricao: '',
  dataNecessaria: null,
  solicitanteId: solicitanteId || null,
  enderecoTipo: 'RESIDENCIAL',
  endereco: {},
  items: [],
});

const ENTREGAS_TAB_INDEX = 2;

const toDraftItem = (item) => ({
  id: item.id,
  tipoItem: item.tipoItem,
  idItem: item.idItem,
  nomeItem: item.nomeItem,
  quantidade: item.quantidade,
  observacao: item.observacao || '',
  motivo: item.motivo || '',
  tamanho: item.tamanho || null,
  arquivos: item.arquivos || [],
});

const arquivoSignature = (arquivo) =>
  [arquivo?.id, arquivo?.nome, arquivo?.extensao]
    .filter((part) => part != null && part !== '')
    .join(':');

const draftSignature = (source) => JSON.stringify({
  descricao: source?.descricao || '',
  dataNecessaria: source?.dataNecessaria
    ? moment(source.dataNecessaria).format('YYYY-MM-DD')
    : null,
  solicitanteId: source?.solicitanteId || null,
  items: (source?.items || []).map((item) => ({
    id: item.id,
    tipoItem: item.tipoItem,
    idItem: String(item.idItem || ''),
    quantidade: item.quantidade == null || item.quantidade === '' ? null : Number(item.quantidade),
    observacao: item.observacao || '',
    motivo: item.motivo || '',
    tamanho: item.tamanho || null,
    arquivos: (item.arquivos || []).map(arquivoSignature),
  })),
});

const Formulario = ({
  open,
  id,
  editing,
  onClose,
  onStartEdit,
  onSaved,
  onExitEdit,
  authData,
  moduleConfigurations,
  selectedRequisicao,
  catalogoItens,
  findRequisicaoById,
  fetchRequisicaoCatalogo,
  createRequisicao,
  updateRequisicao,
  requisicaoClearSelected,
  aprovarRequisicaoParcial,
  cancelarRequisicao,
  criarRequisicaoEntregas,
  atualizarRequisicaoEntrega,
  cancelarRequisicaoEntrega,
  despacharRequisicaoEntrega,
  finalizarRequisicaoEntrega,
  disponibilidade,
  disponibilidadeLoading,
  disponibilidadeError,
  fetchRequisicaoItemDisponibilidade,
  requisicaoClearDisponibilidade,
}) => {
  const isAdmin = useMemo(
    () => isRequisicaoAdmin(moduleConfigurations, authData),
    [moduleConfigurations, authData]
  );
  const isCreate = open && !id;
  const [currentTab, setCurrentTab] = useState(0);
  const [draft, setDraft] = useState(emptyCreate);
  const [editBaseline, setEditBaseline] = useState(null);
  const [itemErrors, setItemErrors] = useState({});
  const [solicitanteError, setSolicitanteError] = useState(false);
  const [enderecoError, setEnderecoError] = useState(false);
  const [itemDecisao, setItemDecisao] = useState(null);
  const [openCancelar, setOpenCancelar] = useState(false);
  const [openCriarEntrega, setOpenCriarEntrega] = useState(false);
  const [criandoEntrega, setCriandoEntrega] = useState(false);
  const [entregaEdicao, setEntregaEdicao] = useState(null);
  const [salvandoEntrega, setSalvandoEntrega] = useState(false);
  const [entregaCancelar, setEntregaCancelar] = useState(null);
  const [cancelandoEntrega, setCancelandoEntrega] = useState(false);
  const [openDespachar, setOpenDespachar] = useState(false);
  const [despachando, setDespachando] = useState(false);
  const [entregaAlvo, setEntregaAlvo] = useState(null);
  const [openComoFunciona, setOpenComoFunciona] = useState(false);
  const [decidindoItem, setDecidindoItem] = useState(false);
  const [openDescartar, setOpenDescartar] = useState(false);
  const [discardIntent, setDiscardIntent] = useState('close');

  const values =
    !isCreate &&
    selectedRequisicao &&
    String(selectedRequisicao.id || '') === String(id || '')
      ? selectedRequisicao
      : {};
  const editLoaded = !!(
    editing
    && values.id
    && String(values.id) === String(id || '')
  );
  const isEditable = isCreate || (
    editing && (
      !editLoaded
      || (
        isRequisicaoAguardando(values.status)
        && isRequisicaoCriador(values, authData?.id, authData?.nome)
      )
    )
  );
  const items = isEditable ? draft.items : getRequisicaoItems(values);
  const catalogoVisivel = useMemo(
    () => (isCreate ? (catalogoItens || []) : catalogoDaArea(catalogoItens, values.area)),
    [catalogoItens, isCreate, values.area]
  );
  const pedidoSeparado = isCreate && areasDoRascunho(catalogoItens, draft.items).length > 1;
  const canOperate = !!values.id && canOperarAreaRequisicao(
    moduleConfigurations,
    authData,
    values.area
  );
  const canDecide = canOperate && canDecidirItens(values.status) && !isEditable;
  const isOwner = isRequisicaoCriador(values, authData?.id, authData?.nome);
  const aguardando = isRequisicaoAguardando(values.status);
  const canCancel = !!values.id && !isEditable && (
    aguardando ? (isOwner || canOperate) : (canOperate && !isRequisicaoTerminal(values.status))
  );
  const canEditFromView = !isEditable && aguardando && isOwner;
  const entregasLiberadas =
    canCriarRequisicaoEntrega(values) || hasEntregaCriada(values);
  const entregaAtencao = getEntregaAtencao(values);
  const statusHelp = getRequisicaoStatusHelp(values);
  const sessionKeyRef = useRef(null);
  const entregaTabLockRef = useRef(0);

  useEffect(() => {
    if (!open) {
      sessionKeyRef.current = null;
      return;
    }
    const sessionKey = isCreate ? 'novo' : `${id || ''}:${editing ? 'edit' : 'view'}`;
    if (sessionKeyRef.current === sessionKey) return;
    sessionKeyRef.current = sessionKey;
    setCurrentTab(0);
    if (isCreate) {
      setDraft(emptyCreate(isAdmin ? authData?.id : null));
      setItemErrors({});
      setSolicitanteError(false);
      setEnderecoError(false);
      fetchRequisicaoCatalogo();
      return;
    }
    if (id) {
      findRequisicaoById(id);
      if (editing) fetchRequisicaoCatalogo();
    }
  }, [open, id, isCreate, editing, isAdmin, authData, findRequisicaoById, fetchRequisicaoCatalogo]);

  const editSeedRef = useRef(null);

  useEffect(() => {
    if (!open || !editing || !editLoaded) {
      if (!open || !editing) editSeedRef.current = null;
      return;
    }
    if (!isRequisicaoAguardando(values.status) || !isRequisicaoCriador(values, authData?.id, authData?.nome)) {
      return;
    }
    if (editSeedRef.current === String(id)) return;
    editSeedRef.current = String(id);
    const nextDraft = {
      descricao: values.descricao || '',
      dataNecessaria: values.dataNecessaria || null,
      solicitanteId: values.solicitanteId || null,
      items: getRequisicaoItems(values).map(toDraftItem),
    };
    setDraft(nextDraft);
    setEditBaseline(draftSignature(nextDraft));
    setItemErrors({});
    setSolicitanteError(false);
  }, [open, editing, editLoaded, id, values, authData]);

  useEffect(() => {
    if (!open || !isCreate || !isAdmin || !authData?.id) return;
    setDraft((current) => (
      current.solicitanteId ? current : { ...current, solicitanteId: authData.id }
    ));
  }, [open, isCreate, isAdmin, authData]);

  useEffect(() => {
    if (!itemDecisao || !values.id) {
      requisicaoClearDisponibilidade();
      return;
    }
    fetchRequisicaoItemDisponibilidade(values.id, itemDecisao.id);
  }, [
    itemDecisao,
    values.id,
    fetchRequisicaoItemDisponibilidade,
    requisicaoClearDisponibilidade,
  ]);

  useEffect(() => {
    if (entregaTabLockRef.current > 0) {
      entregaTabLockRef.current = 0;
      if (currentTab !== ENTREGAS_TAB_INDEX) setCurrentTab(ENTREGAS_TAB_INDEX);
      return;
    }
    if (!entregasLiberadas && currentTab === ENTREGAS_TAB_INDEX) {
      setCurrentTab(0);
    }
  }, [selectedRequisicao, entregasLiberadas, currentTab]);

  const refresh = () => {
    if (id) findRequisicaoById(id);
  };

  const onCloseLocal = (isSave) => {
    requisicaoClearSelected();
    setDraft(emptyCreate());
    setEditBaseline(null);
    setItemDecisao(null);
    setItemErrors({});
    setSolicitanteError(false);
    setEnderecoError(false);
    setOpenCancelar(false);
    setOpenCriarEntrega(false);
    setCriandoEntrega(false);
    setEntregaEdicao(null);
    setSalvandoEntrega(false);
    setEntregaCancelar(null);
    setCancelandoEntrega(false);
    setOpenDespachar(false);
    setDespachando(false);
    setEntregaAlvo(null);
    setOpenComoFunciona(false);
    setDecidindoItem(false);
    setOpenDescartar(false);
    onClose(isSave);
  };

  const handleDecidirItem = (payload) => {
    if (!itemDecisao?.id || !values.id || decidindoItem) return;
    setDecidindoItem(true);
    aprovarRequisicaoParcial(values.id, { itens: [payload] }, (ok) => {
      setDecidindoItem(false);
      if (!ok) return;
      setItemDecisao(null);
      refresh();
    });
  };

  const buildPayload = (requisicaoId, status, dataSolicitacao, { endereco = false } = {}) => ({
    id: requisicaoId,
    status,
    dataSolicitacao,
    descricao: draft.descricao || null,
    dataNecessaria: draft.dataNecessaria
      ? moment(draft.dataNecessaria).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
      : null,
    ...(isAdmin ? { solicitanteId: draft.solicitanteId } : {}),
    ...(endereco ? { enderecoEntrega: enderecoEntregaPayload(draft) } : {}),
    items: draft.items.map((item) => ({
      id: item.id,
      tipoItem: item.tipoItem,
      idItem: item.idItem,
      nomeItem: item.nomeItem,
      quantidade: item.quantidade,
      observacao: item.observacao || null,
      motivo: item.motivo,
      tamanho: item.tamanho || null,
    })),
    arquivos: draft.items.flatMap((item) =>
      (item.arquivos || []).map((arquivo) => ({
        ...arquivo,
        entityId: item.id,
      }))
    ),
  });

  const validateDraft = () => {
    const missingSolicitante = isAdmin && !draft.solicitanteId;
    const missingItems = !draft.items.length;
    const missingEndereco = isCreate && enderecoOutroIncompleto(draft);
    if (missingSolicitante || missingItems || missingEndereco) {
      setSolicitanteError(missingSolicitante);
      setItemErrors(missingItems ? { items: true } : {});
      setEnderecoError(missingEndereco);
      return false;
    }
    setSolicitanteError(false);
    setItemErrors({});
    setEnderecoError(false);
    return true;
  };

  const handleCreate = () => {
    if (!validateDraft()) return;
    createRequisicao(
      buildPayload(uuidv4(), 'AGUARDANDO', moment().format('YYYY-MM-DDTHH:mm:ssZ'), { endereco: true }),
      (ok) => {
        if (ok) onCloseLocal(true);
      }
    );
  };

  const handleUpdate = () => {
    if (!id || !validateDraft()) return;
    updateRequisicao(
      id,
      buildPayload(id, values.status || 'AGUARDANDO', values.dataSolicitacao),
      (ok) => {
        if (!ok) return;
        if (onSaved) onSaved();
      }
    );
  };

  const startEdit = () => {
    if (!canEditFromView) return;
    editSeedRef.current = String(id);
    const nextDraft = {
      descricao: values.descricao || '',
      dataNecessaria: values.dataNecessaria || null,
      solicitanteId: values.solicitanteId || null,
      items: getRequisicaoItems(values).map(toDraftItem),
    };
    setDraft(nextDraft);
    setEditBaseline(draftSignature(nextDraft));
    setItemErrors({});
    setSolicitanteError(false);
    fetchRequisicaoCatalogo();
    if (onStartEdit) onStartEdit();
  };

  const isEditDirty = !!(editBaseline && draftSignature(draft) !== editBaseline);

  const exitEdit = () => {
    setOpenDescartar(false);
    setDiscardIntent('close');
    setEditBaseline(null);
    editSeedRef.current = null;
    setItemErrors({});
    setSolicitanteError(false);
    if (onExitEdit) onExitEdit();
  };

  const requestExitEdit = () => {
    if (isEditDirty) {
      setDiscardIntent('exit');
      setOpenDescartar(true);
      return;
    }
    exitEdit();
  };

  const requestClose = () => {
    if (!isCreate && isEditable && isEditDirty) {
      setDiscardIntent('close');
      setOpenDescartar(true);
      return;
    }
    onCloseLocal(false);
  };

  const headerAction = isCreate
    ? []
    : isEditable
      ? [
        {
          title: 'Cancelar edição',
          onClick: requestExitEdit,
          variant: 'outlined',
        },
      ]
      : [
        {
          title: 'Cancelar',
          onClick: () => setOpenCancelar(true),
          show: canCancel,
          color: 'error',
        },
        {
          title: 'Entenda',
          onClick: () => setOpenComoFunciona(true),
          variant: 'outlined',
        },
        {
          title: 'Editar',
          onClick: startEdit,
          show: canEditFromView,
        },
      ];

  const itensTab = (
    <ItensTab
      items={items}
      requisicao={values}
      canDecide={canDecide}
      errors={itemErrors}
      canEditItems={isEditable}
      catalogoItens={catalogoVisivel}
      onDecidir={(item) => setItemDecisao(item)}
      onItemsChange={(nextItems) => {
        setItemErrors({});
        setDraft((current) => ({ ...current, items: nextItems }));
      }}
    />
  );

  return (
    <>
      <DialogDefaultV2
        open={open}
        handlerCancel={requestClose}
        handlerConfirm={isCreate ? handleCreate : isEditable ? handleUpdate : () => onCloseLocal(false)}
        title={
          isCreate
            ? 'Nova requisição'
            : values.numero
              ? `Requisição ${values.numero}`
              : 'Requisição'
        }
        confirmMessage={isCreate ? 'Enviar' : isEditable ? 'Salvar' : 'Fechar'}
        disabledConfirmButton={isEditable && !isCreate && !isEditDirty}
        maxWidth="lg"
        fullWidth
        readOnly={!isEditable}
        headerAction={headerAction}
      >
        <Grid container spacing={1} sx={{ mt: 0, mb: 1 }}>
          {!isCreate && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <InputV2 label="Número" value={values.numero || '—'} disabled />
            </Grid>
          )}
          {!isCreate && values.area && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <InputV2
                label="Área"
                value={REQUISICAO_AREA_LABELS[values.area] || values.area}
                disabled
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: isCreate ? 8 : values.area ? 3 : 5 }}>
            {isEditable && isAdmin ? (
              <UsuarioSelectV2
                label="Solicitante"
                isRequired
                ativo
                error={solicitanteError}
                value={draft.solicitanteId || ''}
                onSelect={(solicitanteId) => {
                  setSolicitanteError(false);
                  setDraft((current) => ({ ...current, solicitanteId }));
                }}
              />
            ) : (
              <InputV2
                label="Solicitante"
                value={
                  isCreate
                    ? authData?.nome || '—'
                    : values.solicitanteNome || '—'
                }
                disabled
              />
            )}
          </Grid>
          {!isCreate && (
            <Grid size={{ xs: 12, sm: values.area ? 3 : 4 }}>
              <InputV2
                label="Status"
                value={getRequisicaoStatusLabel(values.status)}
                disabled
                useTooltip={!!statusHelp}
                title={statusHelp}
              />
            </Grid>
          )}
          {isCreate ? (
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePickerV2
                label="Data necessária"
                value={draft.dataNecessaria}
                onChange={(dataNecessaria) =>
                  setDraft((current) => ({ ...current, dataNecessaria }))
                }
              />
            </Grid>
          ) : isEditable ? (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputV2
                  label="Data da solicitação"
                  value={formatDate(values.dataSolicitacao)}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePickerV2
                  label="Data necessária"
                  value={draft.dataNecessaria}
                  onChange={(dataNecessaria) =>
                    setDraft((current) => ({ ...current, dataNecessaria }))
                  }
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputV2
                  label="Data da solicitação"
                  value={formatDate(values.dataSolicitacao)}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputV2
                  label="Data necessária"
                  value={formatDate(values.dataNecessaria)}
                  disabled
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12 }}>
            <InputV2
              label="Contexto"
              name="descricao"
              value={isEditable ? draft.descricao : values.descricao || '—'}
              disabled={!isEditable}
              multiline
              rows={2}
              onChange={(event) =>
                setDraft((current) => ({ ...current, descricao: event.target.value }))
              }
            />
          </Grid>
          {isCreate && (
            <EnderecoEntregaFields
              tipo={draft.enderecoTipo}
              endereco={draft.endereco}
              error={enderecoError}
              onTipo={(enderecoTipo) => {
                setEnderecoError(false);
                setDraft((current) => ({ ...current, enderecoTipo }));
              }}
              onEndereco={(patch) => {
                setEnderecoError(false);
                setDraft((current) => ({
                  ...current,
                  endereco: { ...(current.endereco || {}), ...patch },
                }));
              }}
            />
          )}
          {!isCreate && formatEnderecoEntrega(values.enderecoEntrega) && (
            <Grid size={{ xs: 12 }}>
              <InputV2
                label="Endereço de entrega"
                value={formatEnderecoEntrega(values.enderecoEntrega)}
                disabled
              />
            </Grid>
          )}
        </Grid>
        {pedidoSeparado && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Este pedido será separado em duas requisições: Almoxarifado e Drone.
          </Alert>
        )}
        {isEditable ? itensTab : (
          <TabFormV2
            currentTab={currentTab}
            onChangeTab={setCurrentTab}
            tabPanels={[
              { id: 'itens', label: 'Itens', element: itensTab },
              {
                id: 'pedidos',
                label: 'Pedidos + Histórico',
                element: (
                  <PedidosTab requisicao={values} />
                ),
              },
              {
                id: 'entregas',
                label: entregaAtencao ? (
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                  >
                    Entregas
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                ) : 'Entregas',
                disabled: !entregasLiberadas,
                infoTooltip: entregasLiberadas
                  ? null
                  : 'Entregas é o último passo. Decida todos os itens na aba Itens antes de criar uma entrega.',
                element: (
                  <EntregasTab
                    entregas={values.entregas || []}
                    requisicao={values}
                    canManage={canOperate}
                    onCriar={() => {
                      setEntregaEdicao(null);
                      setOpenCriarEntrega(true);
                    }}
                    onEditar={(entrega) => {
                      setOpenCriarEntrega(false);
                      setEntregaEdicao(entrega);
                    }}
                    onCancelar={(entrega) => setEntregaCancelar(entrega)}
                    onDespachar={(entrega) => {
                      setEntregaAlvo(entrega);
                      setOpenDespachar(true);
                    }}
                    onFinalizar={(entrega) => {
                      finalizarRequisicaoEntrega(entrega.id, (ok) => {
                        if (ok) refresh();
                      });
                    }}
                  />
                ),
              },
            ]}
          />
        )}
      </DialogDefaultV2>
      <ComoFuncionaDialog
        open={openComoFunciona}
        onClose={() => setOpenComoFunciona(false)}
      />
      <DecisaoItemDialog
        open={!!itemDecisao}
        item={itemDecisao}
        onClose={() => {
          if (decidindoItem) return;
          setItemDecisao(null);
        }}
        onConfirm={handleDecidirItem}
        confirmLoading={decidindoItem}
        disponibilidade={disponibilidade}
        disponibilidadeLoading={disponibilidadeLoading}
        disponibilidadeError={disponibilidadeError}
        catalogoItens={catalogoItens}
      />
      <DialogDefaultV2
        open={openDescartar}
        handlerCancel={() => setOpenDescartar(false)}
        handlerConfirm={() => {
          if (discardIntent === 'exit') {
            exitEdit();
            return;
          }
          onCloseLocal(false);
        }}
        title={discardIntent === 'exit' ? 'Cancelar edição?' : 'Descartar edições?'}
        confirmMessage="Descartar"
        cancelMessage="Voltar"
        confirmColor="error"
        maxWidth="xs"
        fullWidth
        useFullScreen={false}
      >
        <Typography variant="body2">
          {discardIntent === 'exit'
            ? 'Você fez edições. Tem certeza que deseja cancelar a edição? Você vai perder essas edições.'
            : 'Você fez edições. Tem certeza que deseja cancelar? Você vai perder essas edições.'}
        </Typography>
      </DialogDefaultV2>
      <MotivoDialog
        open={openCancelar}
        title="Cancelar requisição"
        label="Motivo"
        confirmMessage="Cancelar"
        onClose={() => setOpenCancelar(false)}
        onConfirm={(motivo) => {
          setOpenCancelar(false);
          cancelarRequisicao(values.id, { motivo }, (ok) => {
            if (ok) onCloseLocal(true);
          });
        }}
      />
      <CriarEntregasDialog
        open={openCriarEntrega || !!entregaEdicao}
        requisicao={values}
        entrega={entregaEdicao}
        submitting={entregaEdicao ? salvandoEntrega : criandoEntrega}
        onClose={() => {
          if (criandoEntrega || salvandoEntrega) return;
          setOpenCriarEntrega(false);
          setEntregaEdicao(null);
        }}
        onConfirm={(data) => {
          if (entregaEdicao) {
            if (salvandoEntrega) return;
            const entregaId = entregaEdicao.id;
            setSalvandoEntrega(true);
            atualizarRequisicaoEntrega(entregaId, data, (ok) => {
              setSalvandoEntrega(false);
              if (!ok) return;
              entregaTabLockRef.current += 1;
              setCurrentTab(ENTREGAS_TAB_INDEX);
              setEntregaEdicao(null);
              refresh();
            });
            return;
          }
          if (criandoEntrega) return;
          setCriandoEntrega(true);
          criarRequisicaoEntregas(values.id, data, (ok) => {
            setCriandoEntrega(false);
            if (!ok) return;
            entregaTabLockRef.current += 1;
            setCurrentTab(ENTREGAS_TAB_INDEX);
            setOpenCriarEntrega(false);
            refresh();
          });
        }}
      />
      <DialogDefaultV2
        open={!!entregaCancelar}
        handlerCancel={() => {
          if (cancelandoEntrega) return;
          setEntregaCancelar(null);
        }}
        handlerConfirm={() => {
          if (!entregaCancelar || cancelandoEntrega) return;
          const entregaId = entregaCancelar.id;
          setCancelandoEntrega(true);
          cancelarRequisicaoEntrega(entregaId, (ok) => {
            setCancelandoEntrega(false);
            if (!ok) return;
            entregaTabLockRef.current += 1;
            setCurrentTab(ENTREGAS_TAB_INDEX);
            setEntregaCancelar(null);
            refresh();
          });
        }}
        title="Cancelar entrega"
        confirmMessage="Cancelar entrega"
        confirmLoadingMessage="Cancelando…"
        cancelMessage="Voltar"
        confirmColor="error"
        confirmLoading={cancelandoEntrega}
        maxWidth="xs"
        fullWidth
        useFullScreen={false}
      >
        Os itens desta entrega voltam a ficar disponíveis para uma nova entrega. O registro permanece como cancelado.
      </DialogDefaultV2>
      <DespacharDialog
        open={openDespachar}
        entrega={entregaAlvo}
        submitting={despachando}
        onClose={() => {
          if (despachando) return;
          setOpenDespachar(false);
          setEntregaAlvo(null);
        }}
        onConfirm={(data) => {
          const entregaId = entregaAlvo && entregaAlvo.id;
          if (!entregaId || despachando) return;
          setDespachando(true);
          despacharRequisicaoEntrega(entregaId, data, (ok) => {
            setDespachando(false);
            if (!ok) return;
            entregaTabLockRef.current += 1;
            setCurrentTab(ENTREGAS_TAB_INDEX);
            setOpenDespachar(false);
            setEntregaAlvo(null);
            refresh();
          });
        }}
      />
    </>
  );
};

Formulario.propTypes = {
  open: PropTypes.bool,
  id: PropTypes.string,
  editing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onStartEdit: PropTypes.func,
  onSaved: PropTypes.func,
  onExitEdit: PropTypes.func,
  authData: PropTypes.object,
  moduleConfigurations: PropTypes.array,
  selectedRequisicao: PropTypes.object,
  catalogoItens: PropTypes.array,
  disponibilidade: PropTypes.object,
  disponibilidadeLoading: PropTypes.bool,
  disponibilidadeError: PropTypes.bool,
  findRequisicaoById: PropTypes.func.isRequired,
  fetchRequisicaoCatalogo: PropTypes.func.isRequired,
  createRequisicao: PropTypes.func.isRequired,
  updateRequisicao: PropTypes.func.isRequired,
  requisicaoClearSelected: PropTypes.func.isRequired,
  aprovarRequisicaoParcial: PropTypes.func.isRequired,
  cancelarRequisicao: PropTypes.func.isRequired,
  criarRequisicaoEntregas: PropTypes.func.isRequired,
  atualizarRequisicaoEntrega: PropTypes.func.isRequired,
  cancelarRequisicaoEntrega: PropTypes.func.isRequired,
  despacharRequisicaoEntrega: PropTypes.func.isRequired,
  finalizarRequisicaoEntrega: PropTypes.func.isRequired,
  fetchRequisicaoItemDisponibilidade: PropTypes.func.isRequired,
  requisicaoClearDisponibilidade: PropTypes.func.isRequired,
};

Formulario.defaultProps = {
  open: false,
  id: null,
  editing: false,
  onStartEdit: null,
  onSaved: null,
  onExitEdit: null,
  authData: {},
  moduleConfigurations: [],
  selectedRequisicao: {},
  catalogoItens: [],
  disponibilidade: null,
  disponibilidadeLoading: false,
  disponibilidadeError: false,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
  selectedRequisicao: selectorsRequisicao.getSelectedRequisicao(state),
  catalogoItens: selectorsRequisicao.getCatalogoItens(state),
  disponibilidade: selectorsRequisicao.getDisponibilidade(state),
  disponibilidadeLoading: selectorsRequisicao.getDisponibilidadeLoading(state),
  disponibilidadeError: selectorsRequisicao.getDisponibilidadeError(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsRequisicao }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Formulario));
